import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import Card, {
	CardBody,
	CardHeader,
	CardLabel,
	CardTitle,
	CardActions,
} from '../../components/bootstrap/Card';
import Button from '../../components/bootstrap/Button';
import PageWrapper from '../../layout/PageWrapper/PageWrapper';
import Page from '../../layout/Page/Page';
import SubHeader, { SubHeaderLeft } from '../../layout/SubHeader/SubHeader';
import { selectLoginResponse } from '../../redux/login/login.selector';
import Icon from '../../components/icon/Icon';
import TableSpinner from '../../common/table/TableSpinner';
import { getMonthlyCheckoutReportStart, getMonthlyCheckoutDetailsStart } from '../../redux/NewReport/NewReport.actions';
import { selectMonthlyCheckoutReportResponse, selectMonthlyCheckoutDetailsReportResponse } from '../../redux/NewReport/NewReport.selectors';
import Select from '../../components/bootstrap/forms/Select';
import Modal, { ModalBody, ModalHeader, ModalTitle } from '../../components/bootstrap/Modal';

interface CheckoutReportRow {
	sNo: number;
	employeeID: string;
	empID: string;
	employeeName: string;
	Designation: string;
	TotalWorkingDays: number;
	TotalPresent: number;
	LateCheckIN: number;
	EarlyCheckOut: number;
	AutoCheckout: number;
	WorkingDays: number;
	AbsentDays: number;
	TotalLeaves: number;
}

interface MonthOption {
	value: string;
	text: string;
}

interface DetailItem {
	date: string;
	day: string;
	formatted_date: string;
}

const months: MonthOption[] = [
	{ value: '01', text: 'January' },
	{ value: '02', text: 'February' },
	{ value: '03', text: 'March' },
	{ value: '04', text: 'April' },
	{ value: '05', text: 'May' },
	{ value: '06', text: 'June' },
	{ value: '07', text: 'July' },
	{ value: '08', text: 'August' },
	{ value: '09', text: 'September' },
	{ value: '10', text: 'October' },
	{ value: '11', text: 'November' },
	{ value: '12', text: 'December' },
];

const years: MonthOption[] = [
	{ value: '2025', text: '2025' },
	{ value: '2026', text: '2026' },
];

interface SortConfig {
	key: keyof CheckoutReportRow | null;
	direction: 'asc' | 'desc';
}

const MonthlyCheckoutReport = () => {
	// Add CSS for sortable headers
	React.useEffect(() => {
		const style = document.createElement('style');
		style.textContent = `
            .sortable-header {
                background-color: #f8f9fa !important;
                font-weight: 600 !important;
                color: #495057 !important;
            }
            .sortable-header:hover {
                background-color: #e9ecef !important;
            }
            th[style*="cursor: pointer"]:hover {
                background-color: #f8f9fa !important;
                color: #495057 !important;
            }
        `;
		document.head.appendChild(style);

		return () => {
			document.head.removeChild(style);
		};
	}, []);

const dispatch = useDispatch();
const loginData = useSelector(selectLoginResponse);
const monthlyCheckoutResp = useSelector(selectMonthlyCheckoutReportResponse);
const monthlyCheckoutDetailsResp = useSelector(selectMonthlyCheckoutDetailsReportResponse);
const getMonthlyCheckoutResponse = Array.isArray(monthlyCheckoutResp?.data) ? monthlyCheckoutResp.data : (Array.isArray(monthlyCheckoutResp) ? monthlyCheckoutResp : []);
const isLoading = monthlyCheckoutResp === null || monthlyCheckoutResp === undefined;
const getMonthlyCheckoutDetailsResponse = Array.isArray(monthlyCheckoutDetailsResp?.data) ? monthlyCheckoutDetailsResp.data : (Array.isArray(monthlyCheckoutDetailsResp) ? monthlyCheckoutDetailsResp : null);
const isDetailsLoading = monthlyCheckoutDetailsResp === null || monthlyCheckoutDetailsResp === undefined;
	const [month, setMonth] = useState((new Date().getMonth() + 1).toString().padStart(2, '0')); // Current month as string
	const [year, setYear] = useState(new Date().getFullYear().toString()); // Current year as string
	const [employeeType, setEmployeeType] = useState<number>(0); // Default to permanent employees (0 = permanent, 1 = temporary)

	// Filter states
	const [employeeNameFilter, setEmployeeNameFilter] = useState('');
	const [employeeCodeFilter, setEmployeeCodeFilter] = useState('');

    // Popover states (contextual near clicked number)
    const [popoverOpen, setPopoverOpen] = useState(false);
    const [popoverTitle, setPopoverTitle] = useState('');
    const [popoverData, setPopoverData] = useState<DetailItem[]>([]);
    const [popoverPos, setPopoverPos] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
    const [currentEmployee, setCurrentEmployee] = useState<CheckoutReportRow | null>(null);

	// Sorting state
	const [sortConfig, setSortConfig] = useState<SortConfig>({
		key: null,
		direction: 'asc',
	});



	// Map UI label to backend detailType
	const mapDetailType = (label: string) => {
		switch (label) {
			case 'Absent Days':
				return 'absent';
			case 'Leave Days':
				return 'leave';
			case 'Late Checkins':
				return 'late';
			case 'Early Checkouts':
				return 'early';
			case 'Auto Checkouts':
				return 'auto';
			default:
				return '';
		}
	};

	// Handle clicking on numbers to show details
    const handleNumberClick = (employee: CheckoutReportRow, detailType: string, count: number, anchorEvent?: React.MouseEvent) => {
		if (count === 0) return; // Don't show modal if count is 0
		
		setCurrentEmployee(employee);
        setPopoverTitle(`${employee.employeeName} - ${detailType}`);
        // Position near the clicked element
        if (anchorEvent) {
            const target = anchorEvent.currentTarget as HTMLElement;
            const rect = target.getBoundingClientRect();
            // Place popover below and aligned to left of the number, with slight offset
            const x = Math.min(rect.left + window.scrollX, window.scrollX + window.innerWidth - 280);
            const y = rect.bottom + window.scrollY + 8;
            setPopoverPos({ x, y });
        }
        // Open popover and clear old data while loading
        setPopoverData([]);
        setPopoverOpen(true);
		
		// Dispatch the details action
    dispatch(getMonthlyCheckoutDetailsStart({
			employeeID: employee.employeeID,
			detailType: mapDetailType(detailType),
			selectedMonth: `${year}-${month}`,
			organisationID: loginData?.organisationID
		}));
	};

	// Handle details response
    React.useEffect(() => {
        if (getMonthlyCheckoutDetailsResponse && Array.isArray(getMonthlyCheckoutDetailsResponse)) {
            setPopoverData(getMonthlyCheckoutDetailsResponse as any);
            // keep popoverOpen as true; data will render
        }
    }, [getMonthlyCheckoutDetailsResponse]);

	// Clickable number component
	const ClickableNumber = ({ employee, detailType, count }: { employee: CheckoutReportRow; detailType: string; count: number | string }) => {
		// Convert to number and check if it's greater than 0
		const numericCount = typeof count === 'string' ? parseInt(count, 10) : count;
		
		if (numericCount === 0 || isNaN(numericCount)) {
			return <span>{count}</span>;
		}
		
		return (
			<span
				style={{ 
					cursor: 'pointer', 
					color: '#007bff', 
					textDecoration: 'underline',
					fontWeight: 'bold',
					userSelect: 'none'
				}}
                onClick={(e) => {
					e.stopPropagation();
                    handleNumberClick(employee, detailType, numericCount, e);
				}}
				title={`Click to view ${detailType.toLowerCase()} details`}
			>
				{count}
			</span>
		);
	};

	// Sorting function
	const handleSort = (key: keyof CheckoutReportRow) => {
		let direction: 'asc' | 'desc' = 'asc';
		if (sortConfig.key === key && sortConfig.direction === 'asc') {
			direction = 'desc';
		}
		setSortConfig({ key, direction });
	};

	// Filter and sort the data based on current configuration
    const filteredAndSortedData = React.useMemo(() => {
        // Normalize response to an array and map backend field names to UI field names
        const rawRows: any[] = Array.isArray(getMonthlyCheckoutResponse)
            ? (getMonthlyCheckoutResponse as any[])
            : [];

        const rows: CheckoutReportRow[] = rawRows.map((r: any) => ({
            sNo: r.sNo ?? r.SNo ?? 0,
            employeeID: String(r.employeeID ?? r.EmployeeID ?? ''),
            empID: String(r.empID ?? r.EmpID ?? ''),
            employeeName: String(r.employeeName ?? r.EmployeeName ?? ''),
            Designation: String(r.Designation ?? ''),
            TotalWorkingDays: Number(r.TotalWorkingDays ?? r.total_working_days ?? 0),
            WorkingDays: Number(r.WorkingDays ?? r.TotalWorkingDays ?? r.total_working_days ?? 0),
            TotalPresent: Number(r.TotalPresent ?? r.present_days ?? 0),
            AbsentDays: Number(r.AbsentDays ?? r.absent_days ?? 0),
            TotalLeaves: Number(r.TotalLeaves ?? r.leave_days ?? 0),
            LateCheckIN: Number(r.LateCheckIN ?? r.late_checkins ?? 0),
            EarlyCheckOut: Number(r.EarlyCheckOut ?? r.early_checkouts ?? 0),
            AutoCheckout: Number(r.AutoCheckout ?? r.auto_checkouts ?? 0),
        }));

		if (!rows || rows.length === 0) {
			return [];
		}

		// First apply filters
		let filteredData = rows.filter((row: CheckoutReportRow) => {
			const nameMatch =
				!employeeNameFilter ||
				row.employeeName?.toLowerCase().includes(employeeNameFilter.toLowerCase());
			const codeMatch =
				!employeeCodeFilter ||
				row.empID?.toLowerCase().includes(employeeCodeFilter.toLowerCase());

			return nameMatch && codeMatch;
		});

		// Then apply sorting if sortConfig.key exists
		if (sortConfig.key) {
			filteredData = [...filteredData].sort((a, b) => {
				const aValue = a[sortConfig.key!];
				const bValue = b[sortConfig.key!];

				// Handle numeric values
				if (typeof aValue === 'number' && typeof bValue === 'number') {
					return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
				}

				// Handle string values
				const aStr = String(aValue).toLowerCase();
				const bStr = String(bValue).toLowerCase();

				if (aStr < bStr) {
					return sortConfig.direction === 'asc' ? -1 : 1;
				}
				if (aStr > bStr) {
					return sortConfig.direction === 'asc' ? 1 : -1;
				}
				return 0;
			});
		}

		return filteredData;
	}, [getMonthlyCheckoutResponse, sortConfig, employeeNameFilter, employeeCodeFilter]);

	// CSV Export function
	const exportToCSV = () => {
		if (!filteredAndSortedData || filteredAndSortedData.length === 0) {
			alert('No data available to export');
			return;
		}

		// Define CSV headers
		const headers = [
			'S.No',
			'Employee Name',
			'Employee Code',
			'Designation',
			'Total Working Days',
			'Present Days',
			'Absent Days',
			'Leave Days',
			'Late Checkins',
			'Early Checkouts',
			'Auto Checkouts',
		];

		// Convert data to CSV format
		const csvContent = [
			headers.join(','),
			...filteredAndSortedData.map((row: CheckoutReportRow, index: number) =>
				[
					index + 1,
					`"${row.employeeName || ''}"`,
					`"${row.empID || ''}"`,
					`"${row.Designation || ''}"`,
					row.TotalWorkingDays || 0,
					row.TotalPresent || 0,
					row.AbsentDays || 0,
					row.TotalLeaves || 0,
					row.LateCheckIN || 0,
					row.EarlyCheckOut || 0,
					row.AutoCheckout || 0,
				].join(','),
			),
		].join('\n');

		// Create and download the file
		const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
		const link = document.createElement('a');
		const url = URL.createObjectURL(blob);
		link.setAttribute('href', url);

		// Generate filename with selected month and year
		const monthName = months.find((m) => m.value === month)?.text || month;
		const filename = `Monthly_Checkout_Report_${monthName}_${year}.csv`;
		link.setAttribute('download', filename);

		link.style.visibility = 'hidden';
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
	};

	useEffect(() => {
		if (loginData?.organisationID) {
            dispatch(
                getMonthlyCheckoutReportStart({
                    // Backend expects numeric month (1-12)
                    selectedMonth: parseInt(month, 10),
                    selectedYear: parseInt(year, 10),
                    organisationID: loginData.organisationID,
                    employeeType: employeeType, // 0 = permanent, 1 = temporary
                }),
            );
		}
	}, [loginData?.organisationID, month, year, employeeType, dispatch]);
    return (
        <>
        <PageWrapper>
			<SubHeader>
				<SubHeaderLeft>
					<Icon icon='Report' size='2x' color='primary' />
					<span className='h4 mb-0 ms-2'>Monthly Report</span>
				</SubHeaderLeft>
			</SubHeader>
			<Page container='fluid'>
				<div className='row'>
					<div className='col-12'>
						<Card stretch>
							<CardHeader>
								<CardLabel icon='FilterList'>
									<CardTitle>Report Filters</CardTitle>
								</CardLabel>
								<CardActions>
									<Button
										color='primary'
										icon='Download'
										onClick={exportToCSV}
										isDisable={
											isLoading ||
											!getMonthlyCheckoutResponse ||
											getMonthlyCheckoutResponse.length === 0
										}>
										Export Report
									</Button>
								</CardActions>
							</CardHeader>
							<CardBody>
								<div className='row g-4 mb-4'>
									<div className='col-md-3'>
										<label htmlFor='month' className='form-label fw-bold'>
											Month
										</label>
										<Select
											id='month'
											value={month}
											onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
												setMonth(e.target.value)
											}
											list={months}
											ariaLabel='Select Month'
										/>
									</div>
									<div className='col-md-3'>
										<label htmlFor='year' className='form-label fw-bold'>
											Year
										</label>
										<Select
											id='year'
											value={year}
											onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
												setYear(e.target.value)
											}
											list={years}
											ariaLabel='Select Year'
										/>
									</div>
									<div className='col-md-3'>
										<label htmlFor='employeeType' className='form-label fw-bold'>
											Employee Type
										</label>
										<Select
											id='employeeType'
											value={employeeType.toString()}
											onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
												setEmployeeType(parseInt(e.target.value, 10))
											}
											list={[
												{ value: '0', text: 'Permanent' },
												{ value: '1', text: 'Temporary' },
											]}
											ariaLabel='Select Employee Type'
										/>
									</div>
									<div className='col-md-3'>
										<label
											htmlFor='employeeNameFilter'
											className='form-label fw-bold'>
											Employee Name
										</label>
										<input
											type='text'
											className='form-control'
											id='employeeNameFilter'
											placeholder='Filter by employee name...'
											value={employeeNameFilter}
											onChange={(e) => setEmployeeNameFilter(e.target.value)}
										/>
									</div>
									<div className='col-md-3'>
										<label
											htmlFor='employeeCodeFilter'
											className='form-label fw-bold'>
											Employee Code
										</label>
										<input
											type='text'
											className='form-control'
											id='employeeCodeFilter'
											placeholder='Filter by employee code...'
											value={employeeCodeFilter}
											onChange={(e) => setEmployeeCodeFilter(e.target.value)}
										/>
									</div>
								</div>
							</CardBody>
						</Card>
					</div>
				</div>
			</Page>
			<>
				{isLoading ? (
					<Card stretch>
						<CardBody>
							<TableSpinner />
						</CardBody>
					</Card>
				) : getMonthlyCheckoutResponse !== null &&
				  getMonthlyCheckoutResponse !== undefined &&
				  getMonthlyCheckoutResponse.length > 0 ? (
					<Card stretch>
						<CardBody>
							<div
								className='table-responsive'
								style={{ maxHeight: '500px', overflowY: 'auto' }}>
								<table className='table table-modern table-hover'>
									<thead
										style={{
											position: 'sticky',
											top: 0,
											backgroundColor: 'white',
											zIndex: 1,
										}}>
										<tr>
											<th>S.No</th>
											<th
												style={{ cursor: 'pointer', userSelect: 'none' }}
												onClick={() => handleSort('employeeName')}
												className={
													sortConfig.key === 'employeeName'
														? 'sortable-header'
														: ''
												}>
												Employee Name
												{sortConfig.key === 'employeeName' && (
													<span style={{ marginLeft: '5px' }}>
														{sortConfig.direction === 'asc' ? '↑' : '↓'}
													</span>
												)}
											</th>
											<th
												style={{ cursor: 'pointer', userSelect: 'none' }}
												onClick={() => handleSort('empID')}
												className={
													sortConfig.key === 'empID'
														? 'sortable-header'
														: ''
												}>
												Employee Code
												{sortConfig.key === 'empID' && (
													<span style={{ marginLeft: '5px' }}>
														{sortConfig.direction === 'asc' ? '↑' : '↓'}
													</span>
												)}
											</th>
											<th
												style={{ cursor: 'pointer', userSelect: 'none' }}
												onClick={() => handleSort('Designation')}
												className={
													sortConfig.key === 'Designation'
														? 'sortable-header'
														: ''
												}>
												Designation
												{sortConfig.key === 'Designation' && (
													<span style={{ marginLeft: '5px' }}>
														{sortConfig.direction === 'asc' ? '↑' : '↓'}
													</span>
												)}
											</th>
											<th
												style={{ cursor: 'pointer', userSelect: 'none' }}
												onClick={() => handleSort('TotalWorkingDays')}
												className={
													sortConfig.key === 'TotalWorkingDays'
														? 'sortable-header'
														: ''
												}>
												Total Working Days
												{sortConfig.key === 'TotalWorkingDays' && (
													<span style={{ marginLeft: '5px' }}>
														{sortConfig.direction === 'asc' ? '↑' : '↓'}
													</span>
												)}
											</th>
											<th
												style={{ cursor: 'pointer', userSelect: 'none' }}
												onClick={() => handleSort('TotalPresent')}
												className={
													sortConfig.key === 'TotalPresent'
														? 'sortable-header'
														: ''
												}>
												Present Days
												{sortConfig.key === 'TotalPresent' && (
													<span style={{ marginLeft: '5px' }}>
														{sortConfig.direction === 'asc' ? '↑' : '↓'}
													</span>
												)}
											</th>
											<th
												style={{ cursor: 'pointer', userSelect: 'none' }}
												onClick={() => handleSort('AbsentDays')}
												className={
													sortConfig.key === 'AbsentDays'
														? 'sortable-header'
														: ''
												}>
												Absent Days
												{sortConfig.key === 'AbsentDays' && (
													<span style={{ marginLeft: '5px' }}>
														{sortConfig.direction === 'asc' ? '↑' : '↓'}
													</span>
												)}
											</th>
											<th
												style={{ cursor: 'pointer', userSelect: 'none' }}
												onClick={() => handleSort('TotalLeaves')}
												className={
													sortConfig.key === 'TotalLeaves'
														? 'sortable-header'
														: ''
												}>
												Leave Days
												{sortConfig.key === 'TotalLeaves' && (
													<span style={{ marginLeft: '5px' }}>
														{sortConfig.direction === 'asc' ? '↑' : '↓'}
													</span>
												)}
											</th>
											<th
												style={{ cursor: 'pointer', userSelect: 'none' }}
												onClick={() => handleSort('LateCheckIN')}
												className={
													sortConfig.key === 'LateCheckIN'
														? 'sortable-header'
														: ''
												}>
												Late Checkins
												{sortConfig.key === 'LateCheckIN' && (
													<span style={{ marginLeft: '5px' }}>
														{sortConfig.direction === 'asc' ? '↑' : '↓'}
													</span>
												)}
											</th>
											<th
												style={{ cursor: 'pointer', userSelect: 'none' }}
												onClick={() => handleSort('EarlyCheckOut')}
												className={
													sortConfig.key === 'EarlyCheckOut'
														? 'sortable-header'
														: ''
												}>
												Early Checkouts
												{sortConfig.key === 'EarlyCheckOut' && (
													<span style={{ marginLeft: '5px' }}>
														{sortConfig.direction === 'asc' ? '↑' : '↓'}
													</span>
												)}
											</th>
											<th
												style={{ cursor: 'pointer', userSelect: 'none' }}
												onClick={() => handleSort('AutoCheckout')}
												className={
													sortConfig.key === 'AutoCheckout'
														? 'sortable-header'
														: ''
												}>
												Auto Checkouts
												{sortConfig.key === 'AutoCheckout' && (
													<span style={{ marginLeft: '5px' }}>
														{sortConfig.direction === 'asc' ? '↑' : '↓'}
													</span>
												)}
											</th>
										</tr>
									</thead>
									<tbody>
                                        {filteredAndSortedData.map(
											(row: CheckoutReportRow, index: number) => (
                                                <tr key={`${row.employeeID || 'emp'}-${row.empID || 'code'}-${index}`}>
													<td>{index + 1}</td>
													<td>{row.employeeName}</td>
													<td>{row.empID}</td>
													<td>{row.Designation}</td>
                                                    <td>{row.TotalWorkingDays ?? 0}</td>
                                                    <td>{row.TotalPresent ?? 0}</td>
													<td>
														<ClickableNumber 
															employee={row} 
															detailType="Absent Days" 
                                                            count={row.AbsentDays ?? 0} 
														/>
													</td>
													<td>
														<ClickableNumber 
															employee={row} 
															detailType="Leave Days" 
                                                            count={row.TotalLeaves ?? 0} 
														/>
													</td>
													<td>
														<ClickableNumber 
															employee={row} 
															detailType="Late Checkins" 
                                                            count={row.LateCheckIN ?? 0} 
														/>
													</td>
													<td>
														<ClickableNumber 
															employee={row} 
															detailType="Early Checkouts" 
                                                            count={row.EarlyCheckOut ?? 0} 
														/>
													</td>
													<td>
														<ClickableNumber 
															employee={row} 
															detailType="Auto Checkouts" 
                                                            count={row.AutoCheckout ?? 0} 
														/>
													</td>
												</tr>
											),
										)}
									</tbody>
								</table>
							</div>
						</CardBody>
					</Card>
				) : (
					<Card stretch>
						<CardBody>
							<div className='text-center py-5'>
								<Icon icon='Info' size='3x' className='mb-3' />
								<h5 className='text-muted'>No Data Available</h5>
								<p className='text-muted'>
									No monthly checkout data found for the selected period.
								</p>
							</div>
						</CardBody>
					</Card>
				)}
			</>
			
			{/* Details Modal */}
            {/* Contextual Popover near clicked number */}
            {popoverOpen ? (
                <div
                    className='card shadow'
                    style={{
                        position: 'fixed',
                        top: popoverPos.y,
                        left: popoverPos.x,
                        zIndex: 2000,
                        width: 260,
                    }}
                    onClick={(e) => e.stopPropagation()}
                >
                    <div className='card-header d-flex justify-content-between align-items-center py-2 px-3'>
                        <span className='h6 mb-0' style={{ fontSize: 14 }}>{popoverTitle}</span>
                        <button
                            type='button'
                            className='btn btn-sm btn-light'
                            onClick={() => setPopoverOpen(false)}
                            aria-label='Close'
                        >
                            ×
                        </button>
                    </div>
                    <div className='card-body p-2'>
                        {isDetailsLoading && popoverData.length === 0 ? (
                            <div className='text-center py-2'>
                                <div className='spinner-border spinner-border-sm' role='status'>
                                    <span className='visually-hidden'>Loading...</span>
                                </div>
                                <p className='mt-2 mb-0 small'>Loading...</p>
                            </div>
                        ) : popoverData.length > 0 ? (
                            <div style={{ maxHeight: 260, overflowY: 'auto' }}>
                                <ul className='list-group list-group-flush mb-0' style={{ minWidth: 220 }}>
                                    <li className='list-group-item py-1 px-2 small fw-semibold text-center bg-light'>Date</li>
                                    {popoverData.map((item: DetailItem, index: number) => (
                                        <li key={index} className='list-group-item py-1 px-2 small text-center'>
                                            {item.formatted_date}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ) : (
                            <div className='text-center py-2'>
                                <Icon icon='Info' size='lg' className='mb-2' />
                                <p className='text-muted mb-0 small'>No details available for this category.</p>
                            </div>
                        )}
                    </div>
                </div>
            ) : <></>}
        </PageWrapper>
        </>
    );
};

export default MonthlyCheckoutReport;
